import * as THREE from 'three';
import { HUD, SIM } from '../config/tuning';
import { TARGETS, TargetKind, WARHEADS, WarheadKind, type TargetKindId, type WarheadKindId } from '../config/targets';
import { bus } from '../core/EventBus';
import { TargetField } from '../combat/TargetField';
import { Terrain } from '../world/Terrain';

/**
 * The impact tuning harness (`?scene=impact`).
 *
 * The M7 counterpart to `?scene=intercept`, and it exists for the same reason: the thing being
 * tuned is *geometry at the moment of arrival* — dive angle, speed, and how far the aim point was
 * missed by — and none of that is legible from a nose camera during the one second it happens.
 *
 * Flying ten kilometres to discover that a bunker needed 50° of dive rather than 45° is an
 * absurd way to learn a threshold. This page answers it in one drag of a slider, by running the
 * **real** `TargetField` sweep and the **real** graded resolution at the real 120 Hz tick — the
 * same swept collision the game uses, not a re-implementation that could drift from it.
 *
 * The run is kinematic rather than flown, exactly as the intercept harness scripts its target's
 * manoeuvre rather than piloting it. What is under test here is the impact model; letting the
 * flight model choose the arrival geometry would only add a second variable to a page whose whole
 * purpose is to isolate the first.
 *
 * The plot is a **side elevation**, because dive angle is a vertical-plane quantity. Cross-track
 * offset — the horizontal half of an aim error — is the one thing a side view cannot show, so it
 * is drawn as an inset plan view beneath.
 */

const PANEL_STYLE = `
  position: fixed; top: 12px; left: 12px; z-index: 20;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.86);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 12px; width: 268px; max-height: calc(100vh - 24px); overflow-y: auto;
`;

const READOUT_STYLE = `
  position: fixed; top: 12px; right: 12px; z-index: 20;
  font: 12px/1.7 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase; white-space: pre;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.86);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 14px; min-width: 300px;
`;

interface Slider {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
}

const SLIDERS = {
  entryRange: { label: 'ENTRY RANGE', min: 200, max: 4000, step: 50, value: 1400, unit: ' M' },
  speed: { label: 'IMPACT SPEED', min: 30, max: 180, step: 1, value: 130, unit: ' M/S' },
  diveDeg: { label: 'DIVE ANGLE', min: -10, max: 85, step: 1, value: 25, unit: '°' },
  /** Lateral aim error: how far the flight line passes to one side of the aim point. */
  crossTrack: { label: 'CROSS-TRACK MISS', min: -30, max: 30, step: 0.5, value: 0, unit: ' M' },
  /** Vertical aim error, applied to where the line is pointed relative to the aim point. */
  verticalBias: { label: 'VERTICAL AIM BIAS', min: -30, max: 30, step: 0.5, value: 0, unit: ' M' },
} as const satisfies Record<string, Slider>;

type SliderKey = keyof typeof SLIDERS;

interface Sample {
  /** Along-track distance remaining to the aim point, metres. */
  rangeM: number;
  /** Height above the aim point, metres. */
  heightM: number;
  crossM: number;
}

interface Result {
  struck: boolean;
  grade: string;
  failure: string;
  effect: number;
  destroyed: boolean;
  aimPointDistanceM: number;
  impactSpeedMs: number;
  diveAngleDeg: number;
  secondaryFireS: number;
  samples: Sample[];
  bodyRadiusM: number;
  directHitRadiusM: number;
  effectRadiusM: number;
  minDiveAngleDeg: number;
  minImpactSpeedMs: number;
  requiresPenetrator: boolean;
  aimPointHeightM: number;
}

export function startImpactScene(canvas: HTMLCanvasElement): void {
  const values = {} as Record<SliderKey, number>;
  for (const key of Object.keys(SLIDERS) as SliderKey[]) values[key] = SLIDERS[key].value;
  let targetKind: TargetKindId = TargetKind.AmmoBunker;
  let warhead: WarheadKindId = WarheadKind.Penetrator;

  const readout = document.createElement('div');
  readout.setAttribute('style', READOUT_STYLE);
  document.body.appendChild(readout);

  const context = canvas.getContext('2d');
  if (context === null) throw new Error('ImpactScene: 2D context unavailable');

  const run = (): void => {
    const result = simulate(values, targetKind, warhead);
    draw(context, canvas, result, values);
    readout.textContent = describe(result, targetKind, warhead);
  };

  buildPanel(values, run, {
    onTarget: (kind) => {
      targetKind = kind;
      run();
    },
    onWarhead: (kind) => {
      warhead = kind;
      run();
    },
    target: () => targetKind,
    warhead: () => warhead,
  });

  const resize = (): void => {
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    run();
  };
  window.addEventListener('resize', resize);
  resize();
}

/**
 * Fly the terminal geometry headless through the real `TargetField`.
 *
 * The aircraft is stepped along a straight line at the simulation's own tick rate rather than
 * teleported to the aim point, because the swept collision is part of what is being tested: a
 * thin target at 140 m/s is exactly the case that a naive end-of-tick check tunnels through
 * (SPEC §4.4), and a harness that skipped the sweep would report a hit the game would miss.
 */
function simulate(
  values: Record<SliderKey, number>,
  targetKind: TargetKindId,
  warheadKind: WarheadKindId,
): Result {
  const resolution = 33;
  const terrain = new Terrain(
    { resolution, worldSizeM: 40000, minHeightM: 0, heightStepM: 0.01 },
    new Float32Array(resolution * resolution),
  );

  const field = new TargetField(terrain);
  field.warhead = warheadKind;
  field.add({ designation: 'HARNESS', kind: targetKind, xM: 0, zM: 0 });
  const target = field.targets[0];
  const params = target.params;

  // Approach direction in the vertical plane, from the chosen dive angle.
  const diveRad = (values.diveDeg * Math.PI) / 180;
  const direction = new THREE.Vector3(Math.cos(diveRad), -Math.sin(diveRad), 0).normalize();

  // Aim point, displaced by the two aim errors. Cross-track is perpendicular in the horizontal
  // plane; the vertical bias moves the aimed point up or down.
  const aimed = target.aimPointM.clone();
  aimed.z += values.crossTrack;
  aimed.y += values.verticalBias;

  const position = aimed.clone().addScaledVector(direction, -values.entryRange);
  const velocity = direction.clone().multiplyScalar(values.speed);
  const previous = position.clone();

  const samples: Sample[] = [];
  const result: Result = {
    struck: false,
    grade: 'NO CONTACT',
    failure: '',
    effect: 0,
    destroyed: false,
    aimPointDistanceM: 0,
    impactSpeedMs: values.speed,
    diveAngleDeg: values.diveDeg,
    secondaryFireS: 0,
    samples,
    bodyRadiusM: params.bodyRadiusM,
    directHitRadiusM: params.directHitRadiusM,
    effectRadiusM: WARHEADS[warheadKind].effectRadiusM,
    minDiveAngleDeg: params.minDiveAngleDeg,
    minImpactSpeedMs: params.minImpactSpeedMs,
    requiresPenetrator: params.requiresPenetrator,
    aimPointHeightM: params.aimPointHeightM,
  };

  const off = bus.on('target:impact', (hit) => {
    result.struck = true;
    result.grade = hit.grade;
    result.failure = hit.failure;
    result.effect = hit.effect;
    result.destroyed = hit.destroyed;
    result.aimPointDistanceM = hit.aimPointDistanceM;
    result.impactSpeedMs = hit.impactSpeedMs;
    result.diveAngleDeg = hit.diveAngleDeg;
    result.secondaryFireS = hit.secondaryFireS;
  });

  const toAim = new THREE.Vector3();
  // A generous tick budget: the slowest allowed speed over the longest allowed range.
  const maxTicks = Math.ceil((values.entryRange / Math.max(10, values.speed)) / SIM.FIXED_DT) + 240;
  for (let i = 0; i < maxTicks; i += 1) {
    previous.copy(position);
    position.addScaledVector(velocity, SIM.FIXED_DT);

    toAim.copy(position).sub(target.aimPointM);
    samples.push({
      rangeM: -toAim.dot(direction),
      heightM: position.y - target.aimPointM.y,
      crossM: position.z - target.aimPointM.z,
    });

    if (field.update(SIM.FIXED_DT, previous, position, velocity)) break;
    // Past the target and below the surface: nothing more can happen.
    if (position.y < -50) break;
  }
  off();
  return result;
}

/** The verdict, in the same shape the debrief would state it. */
function describe(result: Result, targetKind: TargetKindId, warheadKind: WarheadKindId): string {
  const params = TARGETS[targetKind];
  const lines = [
    `TARGET     ${params.displayName}`,
    `WARHEAD    ${warheadKind}`,
    '',
    `GRADE      ${result.grade}`,
    `EFFECT     ${(result.effect * 100).toFixed(0)}%`,
    `DESTROYED  ${result.destroyed ? 'YES' : 'NO'}`,
    `AIM MISS   ${result.struck ? `${result.aimPointDistanceM.toFixed(2)} M` : '—'}`,
    `SPEED      ${result.impactSpeedMs.toFixed(0)} M/S`,
    `DIVE       ${result.diveAngleDeg.toFixed(1)}°`,
  ];
  if (result.secondaryFireS > 0) lines.push(`SECONDARY  ${result.secondaryFireS.toFixed(0)} S`);
  if (result.failure !== '' && result.failure !== 'none') {
    lines.push('', `FAILED ON  ${result.failure.replace(/-/g, ' ')}`);
    lines.push(explainFailure(result));
  }
  lines.push(
    '',
    'REQUIREMENTS',
    `BODY       ${result.bodyRadiusM.toFixed(1)} M`,
    `DIRECT HIT ${result.directHitRadiusM.toFixed(1)} M`,
    `EFFECT R   ${result.effectRadiusM.toFixed(1)} M`,
    `MIN SPEED  ${result.minImpactSpeedMs.toFixed(0)} M/S`,
    `MIN DIVE   ${result.minDiveAngleDeg > 0 ? `${result.minDiveAngleDeg}°` : 'ANY'}`,
    `PENETRATOR ${result.requiresPenetrator ? 'REQUIRED' : 'NOT REQUIRED'}`,
  );
  return lines.join('\n');
}

/** Turn the failure code into the sentence a player would need. */
function explainFailure(result: Result): string {
  switch (result.failure) {
    case 'outside-body':
      return `           MISSED THE BODY BY ${(result.aimPointDistanceM - result.bodyRadiusM).toFixed(1)} M`;
    case 'too-slow':
      return `           NEEDS ${result.minImpactSpeedMs.toFixed(0)} M/S, ARRIVED AT ${result.impactSpeedMs.toFixed(0)}`;
    case 'too-shallow':
      return `           NEEDS ${result.minDiveAngleDeg}°, ARRIVED AT ${result.diveAngleDeg.toFixed(0)}°`;
    case 'wrong-warhead':
      return '           THIS TARGET NEEDS A PENETRATOR';
    case 'outside-effect':
      return `           STRUCK, BUT ${result.aimPointDistanceM.toFixed(1)} M IS OUTSIDE THE WARHEAD`;
    default:
      return '';
  }
}

/**
 * Side elevation of the run, with a plan-view inset for cross-track.
 *
 * The target's body radius and direct-hit radius are drawn as rings around the aim point, so the
 * relationship the grading depends on — where the flight line passes relative to those two
 * circles — is the most visible thing on the page.
 */
function draw(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  result: Result,
  values: Record<SliderKey, number>,
): void {
  const w = canvas.width;
  const h = canvas.height;
  ctx.fillStyle = '#050b0d';
  ctx.fillRect(0, 0, w, h);
  if (result.samples.length === 0) return;

  // --- Side elevation, upper 70%.
  const elevH = h * 0.68;
  const spanM = Math.max(values.entryRange * 1.08, 100);
  const vSpanM = Math.max(spanM * (elevH / w) * 1.4, 80);
  // The aim point sits well left of the right edge: the readout panel lives there, and a plot
  // whose most important feature is hidden behind a text box is not a plot.
  const originX = w * 0.58;
  const originY = elevH * 0.72;
  const scale = (w * 0.50) / spanM;

  const px = (rangeM: number): number => originX - rangeM * scale;
  const py = (heightM: number): number => originY - heightM * ((elevH * 0.62) / vSpanM);

  // Ground line at the target's base.
  ctx.strokeStyle = 'rgba(120, 160, 160, 0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, py(-result.aimPointHeightM));
  ctx.lineTo(w, py(-result.aimPointHeightM));
  ctx.stroke();

  // The target: body radius ring and direct-hit ring around the aim point.
  const cx = px(0);
  const cy = py(0);
  ring(ctx, cx, cy, result.bodyRadiusM * scale, 'rgba(255, 138, 96, 0.55)', [4, 4]);
  ring(ctx, cx, cy, result.directHitRadiusM * scale, 'rgba(255, 214, 102, 0.9)', []);
  ring(ctx, cx, cy, result.effectRadiusM * scale, 'rgba(120, 200, 255, 0.35)', [2, 6]);

  // Flight path.
  ctx.strokeStyle = 'rgba(63, 232, 216, 0.95)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let i = 0; i < result.samples.length; i += 1) {
    const s = result.samples[i];
    const x = px(s.rangeM);
    const y = py(s.heightM);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();

  // Impact marker at the last sample.
  const last = result.samples[result.samples.length - 1];
  ctx.fillStyle = result.destroyed ? 'rgba(255, 92, 74, 0.95)' : 'rgba(255, 214, 102, 0.95)';
  ctx.beginPath();
  ctx.arc(px(last.rangeM), py(last.heightM), 4, 0, Math.PI * 2);
  ctx.fill();

  label(ctx, 'SIDE ELEVATION', 12, elevH - 10);
  label(ctx, `AIM POINT +${result.aimPointHeightM.toFixed(1)} M`, cx + 8, cy - 10);
  label(ctx, result.grade, px(last.rangeM) + 8, py(last.heightM) + 4, result.destroyed ? '#ff5c4a' : '#ffd666');

  // --- Bullseye inset, lower band: the target seen head-on down the flight line.
  //
  // A plan view shows only the cross-track half of an aim error, which leaves the vertical half
  // with nowhere to read. Looking down the approach axis shows both at once, at ring scale, which
  // is the view that actually answers "would this have been a direct hit".
  const eyeY = elevH + (h - elevH) * 0.5;
  const eyeX = w * 0.5;
  ctx.strokeStyle = 'rgba(120, 160, 160, 0.3)';
  ctx.beginPath();
  ctx.moveTo(0, elevH);
  ctx.lineTo(w, elevH);
  ctx.stroke();

  const eyeSpanM = Math.max(
    result.bodyRadiusM * 2.6,
    result.effectRadiusM * 2.6,
    Math.abs(values.crossTrack) * 2.4,
    Math.abs(values.verticalBias) * 2.4,
    12,
  );
  const eyeScale = ((h - elevH) * 0.36) / eyeSpanM;
  ring(ctx, eyeX, eyeY, result.effectRadiusM * eyeScale, 'rgba(120, 200, 255, 0.35)', [2, 6]);
  ring(ctx, eyeX, eyeY, result.bodyRadiusM * eyeScale, 'rgba(255, 138, 96, 0.55)', [4, 4]);
  ring(ctx, eyeX, eyeY, result.directHitRadiusM * eyeScale, 'rgba(255, 214, 102, 0.9)', []);

  // Crosshair through the aim point.
  ctx.strokeStyle = 'rgba(120, 160, 160, 0.25)';
  ctx.beginPath();
  ctx.moveTo(eyeX - 40, eyeY); ctx.lineTo(eyeX + 40, eyeY);
  ctx.moveTo(eyeX, eyeY - 40); ctx.lineTo(eyeX, eyeY + 40);
  ctx.stroke();

  // Where the flight line actually passed: the aim error, both axes.
  const hitX = eyeX + values.crossTrack * eyeScale;
  const hitY = eyeY - values.verticalBias * eyeScale;
  ctx.strokeStyle = result.destroyed ? 'rgba(255, 92, 74, 0.95)' : 'rgba(63, 232, 216, 0.95)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(hitX - 6, hitY - 6); ctx.lineTo(hitX + 6, hitY + 6);
  ctx.moveTo(hitX + 6, hitY - 6); ctx.lineTo(hitX - 6, hitY + 6);
  ctx.stroke();

  label(ctx, 'BULLSEYE — LOOKING DOWN THE APPROACH', 12, elevH + 18);
  // Offset and graded miss are different numbers and both are right: grading uses the
  // perpendicular distance from the aim point to the *flight line*, so on a steep approach part
  // of a vertical offset lies along-track and does not count against the aim. Showing both
  // stops that looking like a contradiction.
  const offsetM = Math.hypot(values.crossTrack, values.verticalBias);
  label(ctx, `OFFSET ${offsetM.toFixed(1)} M`, hitX + 10, hitY - 8);
  if (result.struck) {
    label(ctx, `GRADED MISS ${result.aimPointDistanceM.toFixed(2)} M`, hitX + 10, hitY + 6);
  }

  // Legend.
  label(ctx, 'dashed orange = target body', 12, h - 42, 'rgba(255, 138, 96, 0.8)');
  label(ctx, 'solid amber = direct-hit radius', 12, h - 28, 'rgba(255, 214, 102, 0.9)');
  label(ctx, 'dotted blue = warhead effect radius', 12, h - 14, 'rgba(120, 200, 255, 0.7)');
}

function ring(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  colour: string,
  dash: number[],
): void {
  if (!(r > 0.5)) return;
  ctx.strokeStyle = colour;
  ctx.setLineDash(dash);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
}

function label(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  colour = 'rgba(160, 200, 200, 0.75)',
): void {
  ctx.fillStyle = colour;
  ctx.font = `10px ${HUD.fontFamily}`;
  ctx.fillText(text, x, y);
}

function buildPanel(
  values: Record<SliderKey, number>,
  onChange: () => void,
  hooks: {
    onTarget: (kind: TargetKindId) => void;
    onWarhead: (kind: WarheadKindId) => void;
    target: () => TargetKindId;
    warhead: () => WarheadKindId;
  },
): void {
  const panel = document.createElement('div');
  panel.setAttribute('style', PANEL_STYLE);

  const title = document.createElement('div');
  title.textContent = 'IMPACT · ?scene=impact';
  title.style.opacity = '0.65';
  title.style.marginBottom = '8px';
  panel.appendChild(title);

  panel.appendChild(
    select(
      'TARGET',
      Object.values(TargetKind),
      hooks.target(),
      (v) => hooks.onTarget(v as TargetKindId),
    ),
  );
  panel.appendChild(
    select(
      'WARHEAD',
      Object.values(WarheadKind),
      hooks.warhead(),
      (v) => hooks.onWarhead(v as WarheadKindId),
    ),
  );

  for (const key of Object.keys(SLIDERS) as SliderKey[]) {
    const spec = SLIDERS[key];
    const row = document.createElement('div');
    row.style.margin = '8px 0';

    const caption = document.createElement('div');
    caption.style.display = 'flex';
    caption.style.justifyContent = 'space-between';
    const name = document.createElement('span');
    name.textContent = spec.label;
    const readout = document.createElement('span');
    readout.textContent = `${values[key]}${spec.unit ?? ''}`;
    caption.append(name, readout);

    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.value = String(values[key]);
    input.style.width = '100%';
    input.addEventListener('input', () => {
      values[key] = Number(input.value);
      readout.textContent = `${values[key]}${spec.unit ?? ''}`;
      onChange();
    });

    row.append(caption, input);
    panel.appendChild(row);
  }

  const hint = document.createElement('div');
  hint.style.opacity = '0.6';
  hint.style.marginTop = '10px';
  hint.style.textTransform = 'none';
  hint.style.letterSpacing = '0';
  hint.textContent =
    'Runs the real TargetField sweep at 120 Hz. Try the ammo bunker with a fragmentation ' +
    'warhead, then a penetrator, then walk the dive angle through 50°.';
  panel.appendChild(hint);

  document.body.appendChild(panel);
}

function select(
  label: string,
  options: readonly string[],
  value: string,
  onChange: (value: string) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.style.margin = '8px 0';
  const caption = document.createElement('div');
  caption.textContent = label;
  const input = document.createElement('select');
  input.style.width = '100%';
  input.style.background = 'rgba(4, 10, 12, 0.9)';
  input.style.color = HUD.colour;
  input.style.border = '1px solid rgba(63, 232, 216, 0.35)';
  input.style.font = 'inherit';
  for (const option of options) {
    const item = document.createElement('option');
    item.value = option;
    item.textContent = option;
    if (option === value) item.selected = true;
    input.appendChild(item);
  }
  input.addEventListener('change', () => onChange(input.value));
  row.append(caption, input);
  return row;
}
